import { CheckCircle2, Copy, Download } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { OffCanvas } from "@/shared/components/OffCanvas";
import { Button } from "@/shared/components/ui/button";

interface RecoveryCodesOffCanvasProps {
  isOpen: boolean;
  onClose: () => void;
  recoveryCodes: string[];
}

export function RecoveryCodesOffCanvas({
  isOpen,
  onClose,
  recoveryCodes,
}: RecoveryCodesOffCanvasProps) {
  const [copiedAll, setCopiedAll] = useState(false);

  const handleCopyAll = () => {
    const codesText = recoveryCodes.join("\n\n");

    const copy = () => {
      if (navigator.clipboard) {
        return navigator.clipboard.writeText(codesText);
      }
      // Fallback para ambientes sem HTTPS (ex: QA em HTTP)
      const textarea = document.createElement("textarea");
      textarea.value = codesText;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      return Promise.resolve();
    };

    copy()
      .then(() => {
        setCopiedAll(true);
        toast.success("Códigos copiados para a área de transferência");
        setTimeout(() => setCopiedAll(false), 2000);
      })
      .catch(() => {
        toast.error("Não foi possível copiar os códigos. Copie manualmente.");
      });
  };

  const handleDownload = () => {
    const codesText = `Códigos de Recuperação 2FA - Evora Maxx\n\nData: ${new Date().toLocaleDateString("pt-BR")}\n\n${recoveryCodes.join("\n")}\n\nGuarde estes códigos em local seguro. Cada código pode ser usado apenas uma vez.\n`;
    const blob = new Blob([codesText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `recovery-codes-${new Date().getTime()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Arquivo baixado com sucesso");
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <Button variant="outline" onClick={onClose}>
        Concluir
      </Button>
    </div>
  );

  return (
    <OffCanvas
      isOpen={isOpen}
      onClose={onClose}
      title="2FA ativado com sucesso. Guarde seus códigos de recuperação."
      size="md"
      footer={footer}
      hideCloseButton
    >
      <div className="space-y-5">
        {/* Aviso importante */}
        <div className="rounded-lg border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 p-4">
          <div className="flex gap-3">
            <div className="mt-0.5 flex-shrink-0">
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--color-warning)]">
                <span className="text-xs text-white">!</span>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-700">
                <strong>Estes códigos não serão exibidos novamente.</strong>{" "}
                Guarde-os em local seguro. Você poderá usar cada código apenas
                uma vez para acessar sua conta caso não tenha acesso ao seu
                aplicativo autenticador.
              </p>
            </div>
          </div>
        </div>

        {/* Ações rápidas */}
        <div className="flex gap-3">
          <Button
            variant="outline-neutral"
            onClick={handleCopyAll}
            className="h-10 flex-1"
          >
            {copiedAll ? (
              <>
                <CheckCircle2 size={16} className="text-green-600" />
                <span className="text-green-600">Copiado!</span>
              </>
            ) : (
              <>
                <Copy size={16} />
                Copiar todos
              </>
            )}
          </Button>
          <Button
            variant="outline-neutral"
            onClick={handleDownload}
            className="h-10 flex-1"
          >
            <Download size={16} />
            Baixar (.txt)
          </Button>
        </div>

        {/* Lista de códigos */}
        <div>
          <label className="mb-3 block text-sm text-gray-600">
            Códigos de Recuperação ({recoveryCodes.length})
          </label>
          <div className="grid grid-cols-2 gap-3">
            {recoveryCodes.map((code, index) => (
              <div
                key={index}
                className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-center font-mono text-sm text-gray-900"
              >
                {code}
              </div>
            ))}
          </div>
        </div>
      </div>
    </OffCanvas>
  );
}
